// Tiles on a customer's page you can tap (Liam's testers, 19 Oct 2026: "the tiles on the app should be clickable ie price.
// Every 2 weeks should be an option and maybe scroll all the way down is a custom one also arrow on no mobile on file
// doesn't"; drawn https://claude.ai/artifact/YSZ8fzFzGrBDiCgtsdi5u7, agreed with Astra). Price, Every and the missing
// number or email each open a small sheet that saves at once with Undo, through the customer's own PATCH: the price
// and how often at the version shown (never merged over someone else's), the number or email with `base` (same-time
// saves). Every carries cycle_days AND cycle_text together, and its Undo puts both back.
import { pinFoot } from './pin-foot'
import { mutate } from './api'
import { kindLabel } from './trade'
import { firstName } from './late-message'
import { severalJobsOn } from './job-names'
import { callingCode, currencySymbol } from './round-config'
import { cursorOnOpen, keepAboveKeyboard } from './sheet-keyboard'
import type { Customer } from './types'
import { cycleLabel, cycleNoteIsMeaningful, el, money, parseMoney, toast } from './ui'
import { howOftenPicker, oftenKind } from './how-often'
import { undoPatch } from './undo'

// Also the missing-details box (add-details.ts, tidy-up item 8).
export function sheet(title: string): { dialog: HTMLDialogElement; body: HTMLElement; error: HTMLElement; done: () => void } {
  const dialog = el('dialog', { class: 'booking-dialog quick-sheet', 'aria-label': title }) as HTMLDialogElement
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const body = el('div', { class: 'booking-content quick-sheet-body' })
  const error = el('p', { class: 'form-error', role: 'alert' })
  dialog.append(el('header', { class: 'booking-head' }, el('h2', {}, title), close), body)
  const free = keepAboveKeyboard(dialog)
  const done = (): void => { if (dialog.open) dialog.close() }
  close.addEventListener('click', done)
  dialog.addEventListener('close', () => { free(); dialog.remove() })
  document.body.append(dialog)
  dialog.showModal()
  return { dialog, body, error, done }
}

// Whose price or how often this is: with several jobs on, the main job by its name ("Andy's windows").
function whose(c: Customer): string {
  const who = firstName(c.name) || c.name
  return severalJobsOn() ? `${who}'s ${(c.job_name?.trim() || kindLabel('window')).toLowerCase()}` : `${who}'s`
}

async function saveCustomer(c: Customer, label: string, set: Record<string, unknown>, before: Record<string, unknown>, text: string, after: () => void, base?: Record<string, unknown>): Promise<boolean> {
  const path = `/api/customers/${c.id}`
  const version = c.version
  const ack = await mutate({ label, method: 'PATCH', path, body: { version, ...set, ...(base ? { base } : {}) }, onConflict: () => { toast(`${c.name} was changed somewhere else first. Have a look and try again.`); after() } })
  if (ack.ok || ack.status === 0) {
    // The page's copy moves on with the save, so a second tile changed straight after saves at the new version.
    const row = ack.row as { version?: number } | undefined
    Object.assign(c, set, { version: typeof row?.version === 'number' ? row.version : version + 1 })
    // Held for signal still gets its Undo (sent after it, in order).
    undoPatch({ text: ack.ok ? text : `${text}. No signal: saved on this phone, it sends as soon as it can`, label: `Undo ${label}`, path, ack, version, body: before, after })
    after()
    return true
  }
  if (ack.status !== 409) toast(ack.error ?? 'That did not save.')
  return ack.status === 409
}

export function openPriceSheet(c: Customer, after: () => void): void {
  const s = sheet(`${whose(c)} price`)
  const each = el('input', { inputmode: 'decimal', 'aria-label': 'Each clean' }) as HTMLInputElement
  each.value = c.reg_price_pence != null ? (c.reg_price_pence / 100).toFixed(2) : ''
  const first = el('input', { inputmode: 'decimal', placeholder: 'Same as each clean', 'aria-label': 'First clean' }) as HTMLInputElement
  first.value = c.first_clean_price_pence != null ? (c.first_clean_price_pence / 100).toFixed(2) : ''
  const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save') as HTMLButtonElement
  const paint = (): void => { try { const p = parseMoney(each.value); save.textContent = p == null ? 'Save' : `Save ${money(p)}` } catch { save.textContent = 'Save' } }
  each.addEventListener('input', paint)
  paint()
  const box = (label: string, input: HTMLInputElement, note?: string): HTMLElement =>
    el('label', { class: 'booking-field' }, el('span', {}, label, note ? el('small', { class: 'muted' }, ` ${note}`) : ''), el('span', { class: 'fj-money' }, el('span', {}, currencySymbol()), input))
  s.body.append(box('Each clean', each), box('First clean', first, 'optional, if the first one costs more'),
    el('p', { class: 'muted field-help' }, 'Visits already booked keep their price. Change one on its visit.'), s.error, pinFoot(save))
  cursorOnOpen(each)
  save.addEventListener('click', () => {
    let reg: number | null, fc: number | null
    try { reg = parseMoney(each.value); fc = parseMoney(first.value) } catch (err) { s.error.textContent = (err as Error).message; return }
    const set: Record<string, unknown> = {}, before: Record<string, unknown> = {}
    if (reg !== (c.reg_price_pence ?? null)) { set.reg_price_pence = reg; before.reg_price_pence = c.reg_price_pence ?? null }
    if (fc !== (c.first_clean_price_pence ?? null)) { set.first_clean_price_pence = fc; before.first_clean_price_pence = c.first_clean_price_pence ?? null }
    if (!Object.keys(set).length) { s.done(); return }
    save.disabled = true
    void saveCustomer(c, `Price: ${c.name}`, set, before, `${whose(c)} price is ${reg == null ? 'not set' : money(reg)}`, after).then((ok) => { if (ok) s.done(); else save.disabled = false })
  })
}

// How often, the one question (src/how-often.ts; Liam, 27 Oct 2026, design D): Regular, Just once or Ad hoc, then for
// Regular the weeks. It saves as soon as the answer is whole (a tile, or Regular and its number), with Undo. Not set shows
// as no tile picked. A note worth keeping on the old answer stays.
export function openEverySheet(c: Customer, after: () => void): void {
  const s = sheet(`${whose(c)} how often`)
  const keepNote = cycleNoteIsMeaningful(c.cycle_days, c.cycle_text)
  const before = { cycle_days: c.cycle_days ?? null, cycle_text: c.cycle_text ?? null }
  let saving = false
  const was = { days: c.cycle_days ?? null, marker: c.cycle_text ?? null }
  const picker = howOftenPicker({ value: was, question: 'How often?', onChange: (v) => {
    if (!v || saving) return
    if (v.days === (c.cycle_days ?? null) && (v.days || cycleLabel(null, v.marker) === cycleLabel(null, c.cycle_text))) return
    saving = true
    const set = { cycle_days: v.days, cycle_text: v.marker ?? (keepNote ? c.cycle_text ?? null : null) }
    const words = v.days ? `every ${cycleLabel(v.days, null)}` : cycleLabel(null, v.marker)
    void saveCustomer(c, `How often: ${c.name}`, set, before, `${whose(c)} how often: ${words}`, after).then((ok) => { saving = false; if (ok) s.done() })
  } })
  const notSet = !oftenKind(was) ? el('p', { class: 'muted field-help' }, 'Not set yet. Pick one.') : el('p', { class: 'muted field-help' }, 'Tap one. It saves straight away, with Undo.')
  s.body.append(notSet, picker.node, el('p', { class: 'muted field-help' }, 'Their prices stay as they are: change them with the price on their page.'), s.error)
}

// A number as the round writes one (a US round's, not 07700 900123: tidy-up item 8, 20 Oct 2026).
export function phonePlaceholder(): string {
  return callingCode() === '1' ? '(512) 555-0142' : '07700 900123'
}

// The number (or email) missing for the way they are messaged, saved on its own: how they are messaged never changes here.
export function openContactSheet(c: Customer, field: 'phone' | 'email', after: () => void): void {
  const who = firstName(c.name) || c.name
  const s = sheet(field === 'email' ? `${who}'s email` : `${who}'s mobile`)
  const input = el('input', { type: field === 'email' ? 'email' : 'tel', autocomplete: 'off', 'aria-label': field === 'email' ? 'Email' : 'Mobile number', placeholder: field === 'email' ? 'name@example.com' : phonePlaceholder() }) as HTMLInputElement
  const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save') as HTMLButtonElement
  s.body.append(el('label', { class: 'booking-field' }, el('span', {}, field === 'email' ? 'Email' : 'Mobile number'), input), s.error, pinFoot(save))
  cursorOnOpen(input)
  const go = (): void => {
    const value = input.value.trim()
    if (!value) { input.focus(); return }
    save.disabled = true
    const was = (c as unknown as Record<string, unknown>)[field] ?? null
    void saveCustomer(c, `${field === 'email' ? 'Email' : 'Number'} for ${c.name}`, { [field]: value }, { [field]: was }, `${field === 'email' ? 'Email' : 'Number'} added for ${c.name}`, after, { [field]: was })
      .then((ok) => { if (ok) s.done(); else save.disabled = false })
  }
  save.addEventListener('click', go)
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); go() } })
}
